import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import { config } from '../../config/config';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { applyQuillFontSizeStep, ensureQuillFontSizesRegistered } from '../../utils/quillFontSize';

ensureQuillFontSizesRegistered();

interface ResearchDeskScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
  authToken?: string | null;
  authUserId?: number | null;
  authUsername?: string | null;
  onUnauthorized?: () => void;
}

type ResearchDeskNote = {
  id: string;
  createdAt: string;
  category: string;
  title: string;
  html: string;
};

const NOTE_DELETE_UNDO_MS = 6500;

type ResearchDeskScreenshot = {
  id: number;
  createdAtUtc: string;
  sourcePage: string;
  stockSymbol: string;
  title: string;
  thumbDataUrl: string;
};

type ScreenshotCategory = 'All' | 'Summary' | 'Key Metrics' | 'Growth' | 'Back Testing';

const SCREENSHOT_CATEGORIES: Array<Exclude<ScreenshotCategory, 'All'>> = ['Summary', 'Key Metrics', 'Growth', 'Back Testing'];

const ResearchDeskScreen: React.FC<ResearchDeskScreenProps> = ({
  isDarkMode,
  selectedStock,
  authToken,
  authUserId,
  authUsername,
  onUnauthorized,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'notes' | 'screenshots'>('notes');
  const [noteContent, setNoteContent] = useState('');
  const [noteTitle, setNoteTitle] = useState('');
  // Category is required for adding a new note.
  const [noteCategory, setNoteCategory] = useState('');
  const [isNoteCategoryOpen, setIsNoteCategoryOpen] = useState(false);
  const [notes, setNotes] = useState<ResearchDeskNote[]>([]);
  const [isNewNoteCollapsed, setIsNewNoteCollapsed] = useState(false);
  const [isChecklistCollapsed, setIsChecklistCollapsed] = useState(false);
  const [notesSearchQuery, setNotesSearchQuery] = useState('');
  const [collapsedNoteIds, setCollapsedNoteIds] = useState<Record<string, boolean>>({});
  const newNoteEditorRef = useRef<HTMLDivElement | null>(null);
  const lastPointerDownTargetRef = useRef<EventTarget | null>(null);
  const lastFocusedWasSearchRef = useRef(false);

  type PendingNoteDelete = {
    id: string;
    note: ResearchDeskNote;
    index: number;
    timerId: number;
  };
  const [pendingNoteDelete, setPendingNoteDelete] = useState<PendingNoteDelete | null>(null);
  const pendingNoteDeleteRef = useRef<PendingNoteDelete | null>(null);

  const [noteMenuOpenForId, setNoteMenuOpenForId] = useState<string | null>(null);
  const [noteMenuAnchor, setNoteMenuAnchor] = useState<{ top: number; left: number } | null>(null);
  const noteMenuRef = useRef<HTMLDivElement | null>(null);

  // Screenshots filter UI state
  const [screenshotCategoryFilter, setScreenshotCategoryFilter] = useState<ScreenshotCategory>('All');
  const [screenshotFromDateKey, setScreenshotFromDateKey] = useState<string>('');
  const [screenshotToDateKey, setScreenshotToDateKey] = useState<string>('');
  const [screenshotsDateIsCustom, setScreenshotsDateIsCustom] = useState(false);
  const [screenshotDefaultFromDateKey, setScreenshotDefaultFromDateKey] = useState<string>('');
  const [screenshotDefaultToDateKey, setScreenshotDefaultToDateKey] = useState<string>('');

  const [screenshots, setScreenshots] = useState<ResearchDeskScreenshot[]>([]);
  const [screenshotsBusy, setScreenshotsBusy] = useState(false);
  const [screenshotsError, setScreenshotsError] = useState<string | null>(null);

  const screenshotsRef = useRef<ResearchDeskScreenshot[]>([]);
  useEffect(() => {
    screenshotsRef.current = screenshots;
  }, [screenshots]);

  const [deletingScreenshotById, setDeletingScreenshotById] = useState<Record<number, boolean>>({});

  const SCREENSHOT_DELETE_UNDO_MS = 6500;
  type PendingScreenshotDelete = {
    id: number;
    row: ResearchDeskScreenshot;
    index: number;
    timerId: number;
  };
  const [pendingScreenshotDelete, setPendingScreenshotDelete] = useState<PendingScreenshotDelete | null>(null);
  const pendingScreenshotDeleteRef = useRef<PendingScreenshotDelete | null>(null);

  const [openScreenshotId, setOpenScreenshotId] = useState<number | null>(null);
  const [openScreenshotImageUrl, setOpenScreenshotImageUrl] = useState<string | null>(null);
  const [openScreenshotBusy, setOpenScreenshotBusy] = useState(false);
  const [openScreenshotError, setOpenScreenshotError] = useState<string | null>(null);

  const normalizedUsername = (authUsername ?? '').trim().toLowerCase();

  useEffect(() => {
    const isWithinModal = (node: Node | null) => {
      if (!(node instanceof Element)) return false;
      return Boolean(node.closest('[role="dialog"], [aria-modal="true"]'));
    };

    const hasOpenModal = () => Boolean(document.querySelector('[role="dialog"][aria-modal="true"]'));

    const onPointerDown = (e: PointerEvent) => {
      lastPointerDownTargetRef.current = e.target;
    };

    const onFocusIn = (e: FocusEvent) => {
      if (hasOpenModal()) {
        lastFocusedWasSearchRef.current = false;
        return;
      }

      const target = e.target as Node | null;
      if (!target) return;

      if (isWithinModal(target)) {
        lastFocusedWasSearchRef.current = false;
        return;
      }

      if ((target as HTMLElement).id === 'search-input') {
        lastFocusedWasSearchRef.current = true;
        return;
      }

      if (!lastFocusedWasSearchRef.current) return;

      const editorRoot = newNoteEditorRef.current;
      if (!editorRoot || !editorRoot.contains(target)) return;

      const lastPointerTarget = lastPointerDownTargetRef.current as Node | null;
      if (lastPointerTarget && editorRoot.contains(lastPointerTarget)) {
        lastFocusedWasSearchRef.current = false;
        return;
      }

      const searchInput = document.getElementById('search-input') as HTMLInputElement | null;
      if (searchInput) {
        searchInput.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
    };
  }, []);

  const resolveUsernameFromToken = (token: string | null | undefined): string => {
    if (!token) return '';
    const parts = token.split('.');
    if (parts.length < 2) return '';
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const payload = JSON.parse(json) as Record<string, unknown>;
      const raw =
        payload.unique_name ??
        payload.preferred_username ??
        payload.username ??
        payload.name ??
        payload.email ??
        '';
      return String(raw || '').trim().toLowerCase();
    } catch {
      return '';
    }
  };

  const resolveUserIdFromToken = (token: string | null | undefined): number | null => {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const payload = JSON.parse(json) as Record<string, unknown>;
      const raw = payload.userId ?? payload.userid ?? payload.id ?? payload.sub ?? payload.nameid;
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (typeof raw === 'string') {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    } catch {
      return null;
    }
  };

  const resolvedUserId =
    typeof authUserId === 'number' && Number.isFinite(authUserId) && authUserId > 0
      ? authUserId
      : resolveUserIdFromToken(authToken);
  const resolvedUsername = normalizedUsername || resolveUsernameFromToken(authToken);
  const canPersistNotes = !!authToken && (resolvedUserId != null || resolvedUsername.length > 0);
  const canPersistScreenshots = !!authToken && (resolvedUserId != null || resolvedUsername.length > 0);
  const showSavedScreenshots = config.features.showSavedScreenshots;
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const didInitFromServerRef = useRef<string | null>(null);
  const suppressChecklistSaveRef = useRef(false);
  const checklistSaveTimerRef = useRef<number | undefined>(undefined);

  const normalizedStockSymbol = useMemo(() => {
    const sym = String(selectedStock ?? '').trim();
    return sym ? sym.toUpperCase() : '';
  }, [selectedStock]);

  const NOTE_CATEGORIES = useMemo(() => [...RESEARCH_DESK_NOTE_CATEGORIES], []);

  useEffect(() => {
    if (!showSavedScreenshots && activeSubTab === 'screenshots') {
      setActiveSubTab('notes');
    }
  }, [showSavedScreenshots, activeSubTab]);

  // Notes list filter UI state (separate from the new-note category selection).
  const [isNotesCategoryFilterOpen, setIsNotesCategoryFilterOpen] = useState(false);
  const [notesCategoryFilters, setNotesCategoryFilters] = useState<string[]>([]);
  const notesFilterPopoverRef = useRef<HTMLDivElement | null>(null);
  const noteCategoryDropdownRef = useRef<HTMLDivElement | null>(null);

  // Screenshots category dropdown UI state
  const [isScreenshotsCategoryOpen, setIsScreenshotsCategoryOpen] = useState(false);
  const screenshotsCategoryPopoverRef = useRef<HTMLDivElement | null>(null);

  const checklistItems = useMemo(
    () => [
      'Volume > 1M',
      'Revenue Growth > 10%',
      'Operating Income Growth > 10%',
      'Earnings Growth > 10%',
      'Operating Cash Flow Growth > 10%',
      "Analysts' Expectations for Growth > 10%",
      'Net Profit Margin > 10%',
      'Profit Margins are Stable or Growing',
      'ROIC > 15%',
      'Reasonable Long-Term Debt',
      'Reasonable Normalized P/E',
    ],
    []
  );

  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const checkedCount = useMemo(
    () => checklistItems.reduce((acc, label) => acc + (checklist[label] ? 1 : 0), 0),
    [checklist, checklistItems]
  );

  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const canAddNote = noteTitle.trim().length > 0 && !syncBusy;

  const quillRef = useRef<any>(null);
  const lastDetectedNoteDirectionRef = useRef<'rtl' | 'ltr'>('ltr');

  const detectTextDirection = (raw: string, fallback: 'rtl' | 'ltr' = 'ltr'): 'rtl' | 'ltr' => {
    const text = String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text) return fallback;

    const isRTLChar = (ch: string) => /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(ch);
    const isLTRChar = (ch: string) => /[A-Za-z]/.test(ch);

    for (const ch of text) {
      if (/\s/.test(ch)) continue;
      if (isRTLChar(ch)) return 'rtl';
      if (isLTRChar(ch)) return 'ltr';
    }

    return fallback;
  };

  useEffect(() => {
    if (!isNotesCategoryFilterOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsNotesCategoryFilterOpen(false);
    };

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const root = notesFilterPopoverRef.current;
      if (!root) return;
      if (!root.contains(target)) setIsNotesCategoryFilterOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isNotesCategoryFilterOpen]);

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  useEffect(() => {
    if (!isScreenshotsCategoryOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsScreenshotsCategoryOpen(false);
    };

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const root = screenshotsCategoryPopoverRef.current;
      if (!target || !root) return;
      if (!root.contains(target)) setIsScreenshotsCategoryOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isScreenshotsCategoryOpen]);

  useEffect(() => {
    if (!isNoteCategoryOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsNoteCategoryOpen(false);
    };

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const root = noteCategoryDropdownRef.current;
      if (!target || !root) return;
      if (!root.contains(target)) setIsNoteCategoryOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isNoteCategoryOpen]);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: '#research-desk-new-note-toolbar',
        handlers: {
          undo: () => {
            quillRef.current?.getEditor?.()?.history.undo();
          },
          redo: () => {
            quillRef.current?.getEditor?.()?.history.redo();
          },
        },
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true,
      },
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      'bold',
      'italic',
      'underline',
      'strike',
      'size',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'align',
      'direction',
      'color',
      'background',
    ],
    []
  );

  // Auto-detect RTL/LTR based on content (same behavior as Insights),
  // but also persist & re-apply it when collapsing/expanding the editor.
  useEffect(() => {
    if (activeSubTab !== 'notes') return;
    const ref = quillRef;
    if (!ref.current) return;

    const quill = ref.current.getEditor();
    const editorElement = quill.root as HTMLElement;

    const containerElement = editorElement.closest('.ql-container') as HTMLElement | null;

    const isRTLChar = (ch: string) => /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(ch);
    const isLTRChar = (ch: string) => /[A-Za-z]/.test(ch);

    const applyDirection = (dir: 'rtl' | 'ltr') => {
      lastDetectedNoteDirectionRef.current = dir;
      editorElement.style.direction = dir;
      editorElement.style.textAlign = dir === 'rtl' ? 'right' : 'left';
      editorElement.setAttribute('dir', dir);

      // Quill sometimes uses container-level direction for caret behavior.
      if (containerElement) {
        containerElement.style.direction = dir;
        containerElement.setAttribute('dir', dir);
      }

      // Apply to Quill's current line as well (root style alone can be overridden by block formatting).
      try {
        quill.format('direction', dir, 'silent');
        quill.format('align', dir === 'rtl' ? 'right' : 'left', 'silent');

        const len = typeof quill.getLength === 'function' ? Math.max(quill.getLength(), 1) : 1;
        quill.formatLine(0, len, { direction: dir, align: dir === 'rtl' ? 'right' : 'left' }, 'silent');
      } catch {
        // Ignore formatting failures (e.g., editor not ready)
      }
    };

    const applyDirectionFromText = (rawText: string) => {
      const text = String(rawText ?? '');
      // Find first non-whitespace character.
      const firstNonWs = text.replace(/\s+/g, '').charAt(0);
      if (!firstNonWs) {
        // Keep last known direction when toggling collapse with existing content.
        applyDirection(lastDetectedNoteDirectionRef.current);
        return;
      }

      if (isRTLChar(firstNonWs)) {
        applyDirection('rtl');
      } else {
        applyDirection('ltr');
      }
    };

    const handler = () => {
      applyDirectionFromText(quill.getText());
    };

    const onSelectionChange = () => {
      // Ensure direction is re-applied on focus/caret moves, especially after remount.
      if (typeof quill.getLength === 'function' && quill.getLength() <= 1) {
        applyDirection(lastDetectedNoteDirectionRef.current);
      }
    };

    // When the editor is empty, set direction BEFORE the first character is inserted.
    // This ensures Hebrew starts on the right immediately.
    const onKeyDown = (e: KeyboardEvent) => {
      const key = (e as any).key as string | undefined;
      if (!key || key.length !== 1) return;

      const currentText = quill.getText().trim();
      if (currentText.length !== 0) return;

      if (isRTLChar(key)) {
        applyDirection('rtl');
      } else if (isLTRChar(key)) {
        applyDirection('ltr');
      }
    };

    // Prefer beforeinput: it provides the actual inserted character (works better with different keyboard layouts/IME).
    const onBeforeInput = (e: Event) => {
      const ie = e as InputEvent;
      const data = typeof ie.data === 'string' ? ie.data : '';
      if (!data) return;

      const currentText = quill.getText().trim();
      if (currentText.length !== 0) return;

      const ch = data.charAt(0);
      if (isRTLChar(ch)) applyDirection('rtl');
      else if (isLTRChar(ch)) applyDirection('ltr');
    };

    // Attach listener.
    quill.on('text-change', handler);
    quill.on('selection-change', onSelectionChange);
    editorElement.addEventListener('beforeinput', onBeforeInput as any, true);
    editorElement.addEventListener('keydown', onKeyDown, true);

    // Apply immediately after mount/open (Quill can recreate the root on collapse toggle).
    requestAnimationFrame(() => {
      applyDirectionFromText(quill.getText());
    });

    return () => {
      quill.off('text-change', handler);
      quill.off('selection-change', onSelectionChange);
      editorElement.removeEventListener('beforeinput', onBeforeInput as any, true);
      editorElement.removeEventListener('keydown', onKeyDown, true);
    };
  }, [activeSubTab, isNewNoteCollapsed]);

  // Also re-apply direction when the content is set programmatically.
  useEffect(() => {
    if (activeSubTab !== 'notes') return;
    const ref = quillRef;
    if (!ref.current) return;

    const quill = ref.current.getEditor();
    const editorElement = quill.root as HTMLElement;
    const containerElement = editorElement.closest('.ql-container') as HTMLElement | null;
    requestAnimationFrame(() => {
      const text = (noteContent ?? '').trim();
      if (text.length === 0) return;
      const firstChar = text.charAt(0);
      const isRTL = /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(firstChar);
      const dir: 'rtl' | 'ltr' = isRTL ? 'rtl' : 'ltr';
      lastDetectedNoteDirectionRef.current = dir;
      editorElement.style.direction = dir;
      editorElement.style.textAlign = dir === 'rtl' ? 'right' : 'left';
      editorElement.setAttribute('dir', dir);
      if (containerElement) {
        containerElement.style.direction = dir;
        containerElement.setAttribute('dir', dir);
      }

      // Ensure Quill's block formats match the DOM direction.
      try {
        quill.format('direction', dir, 'silent');
        quill.format('align', dir === 'rtl' ? 'right' : 'left', 'silent');
        const len = typeof quill.getLength === 'function' ? Math.max(quill.getLength(), 1) : 1;
        quill.formatLine(0, len, { direction: dir, align: dir === 'rtl' ? 'right' : 'left' }, 'silent');
      } catch {
        // Ignore formatting failures (e.g., editor not ready)
      }
    });
  }, [activeSubTab, noteContent]);

  const containerStyle: React.CSSProperties = {
    padding: '1rem 3rem',
    marginTop: '5px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    minHeight: 'calc(100vh - 15rem)',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
    width: '97.2%'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: '700',
    marginBottom: '1rem',
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: isDarkMode ? '#9ca3af' : '#6b7280',
  };

  const screenshotsHeaderRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  };

  const screenshotsLeftControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flex: '0 0 auto',
  };

  const screenshotsControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  };

  const screenshotsDateInputStyle: React.CSSProperties = {
    height: 32,
    borderRadius: 8,
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    background: isDarkMode ? '#111827' : '#ffffff',
    color: '#0D0D0D',
    padding: '0 10px',
    fontSize: '14px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: '500',
    lineHeight: '20px',
  };

  const screenshotsCategoryDropdownButtonStyle: React.CSSProperties = {
    height: 32,
    borderRadius: 8,
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    background: isDarkMode ? '#111827' : '#ffffff',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    padding: '0 44px 0 10px',
    fontSize: '0.9rem',
    minWidth: 160,
    cursor: 'pointer',
    textAlign: 'left',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    userSelect: 'none',
    outline: 'none',
  };

  const screenshotsCategorySelectWrapStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const screenshotsCategorySelectArrowStyle: React.CSSProperties = {
    position: 'absolute',
    // Increase "right" to move the arrow left toward the text.
    right: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    width: 18,
    height: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const screenshotsCategoryDropdownMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 0.5rem)',
    left: 0,
    minWidth: 220,
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    borderRadius: '8px',
    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
    padding: '0.5rem',
    direction: 'ltr',
    textAlign: 'left',
    zIndex: 1000,
  };

  const screenshotsCategoryDropdownItemStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.45rem 0.6rem',
    cursor: 'pointer',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    fontSize: '0.85rem',
    borderRadius: '6px',
    userSelect: 'none',
    textAlign: 'left',
  };

  const screenshotsCategoryDropdownRadioStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    margin: 0,
    flex: '0 0 auto',
  };

  const noteCategoryDropdownWrapStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const noteCategoryDropdownButtonStyle: React.CSSProperties = {
    padding: '0.5rem 2.25rem 0.5rem 1rem',
    borderRadius: '6px',
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    backgroundColor: isDarkMode ? '#1F2937' : '#F6F6F6',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    userSelect: 'none',
    outline: 'none',
    minWidth: 220,
  };

  const noteCategoryDropdownArrowStyle: React.CSSProperties = {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    width: 18,
    height: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const noteCategoryDropdownMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    border: `1px solid ${isDarkMode ? '#111827' : '#D9D9D9'}`,
    borderRadius: '8px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    padding: '0.5rem 0.75rem',
    direction: 'ltr',
    textAlign: 'left',
    zIndex: 1000,
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    boxSizing: 'border-box',
  };

  const noteCategoryDropdownItemStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    padding: '0.45rem 0.2rem',
    height: '44px',
    cursor: 'pointer',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    userSelect: 'none',
    textAlign: 'left',
  };

  const noteCategoryDropdownCheckboxStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    margin: 0,
    borderRadius: 4,
    border: isDarkMode ? '2px solid #4B5563' : '2px solid #D9D9D9',
    background: isDarkMode ? '#111827' : '#fff',
    appearance: 'none',
    outline: 'none',
    display: 'inline-block',
    verticalAlign: 'middle',
  };

  const renderNoteCategoryCheckbox = (checked: boolean, props: any = {}) => (
    <span
      style={{
        ...noteCategoryDropdownCheckboxStyle,
        border: isDarkMode
          ? checked
            ? '2px solid #3B82F6'
            : '2px solid #4B5563'
          : '2px solid #D9D9D9',
        background: isDarkMode
          ? checked
            ? '#3B82F6'
            : '#111827'
          : '#fff',
        position: 'relative',
      }}
      {...props}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
          <polyline
            points="3,7 5.2,9 9,4.5"
            style={{
              fill: 'none',
              stroke: isDarkMode ? '#111827' : '#2F58F2',
              strokeWidth: 2.2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          />
        </svg>
      )}
    </span>
  );

  const notesFilterDropdownMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    width: '240px',
    border: `1px solid ${isDarkMode ? '#111827' : '#D9D9D9'}`,
    borderRadius: '8px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    padding: '0.5rem 0.75rem',
    direction: 'ltr',
    textAlign: 'left',
    zIndex: 1000,
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    boxSizing: 'border-box',
  };

  const notesFilterDropdownItemStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    padding: '0.45rem 0.2rem',
    height: '44px',
    cursor: 'pointer',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    userSelect: 'none',
    textAlign: 'left',
  };

  const notesFilterDropdownCheckboxStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    margin: 0,
    borderRadius: 4,
    border: isDarkMode ? '2px solid #4B5563' : '2px solid #D9D9D9',
    background: isDarkMode ? '#111827' : '#fff',
    appearance: 'none',
    outline: 'none',
    display: 'inline-block',
    verticalAlign: 'middle',
  };

  // Custom tick rendering for dark mode checked state
  const renderNotesFilterCheckbox = (checked: boolean, props: any = {}) => (
    <span
      style={{
        ...notesFilterDropdownCheckboxStyle,
        border: isDarkMode
          ? checked
            ? '2px solid #3B82F6'
            : '2px solid #4B5563'
          : '2px solid #D9D9D9',
        background: isDarkMode
          ? checked
            ? '#3B82F6'
            : '#111827'
          : '#fff',
        position: 'relative',
      }}
      {...props}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
          <polyline
            points="3,7 5.2,9 9,4.5"
            style={{
              fill: 'none',
              stroke: isDarkMode ? '#111827' : '#2F58F2',
              strokeWidth: 2.2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          />
        </svg>
      )}
    </span>
  );

  const screenshotThumbFrameStyle = useMemo<React.CSSProperties>(() => {
    return {
      borderRadius: 6,
      overflow: 'hidden',
      background: isDarkMode ? '#0b1220' : '#ffffff',
      flex: '1 1 auto',
      minHeight: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  }, [isDarkMode]);

  const screenshotIconBaseStyle = useMemo<React.CSSProperties>(() => {
    return {
      width: 28,
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      padding: 0,
      appearance: 'none',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      boxShadow: 'none',
      color: isDarkMode ? '#E5E7EB' : '#111827',
    };
  }, [isDarkMode]);

  const screenshotExpandButtonStyle = useMemo<React.CSSProperties>(() => {
    return {
      ...screenshotIconBaseStyle,
    };
  }, [isDarkMode, screenshotIconBaseStyle]);

  const screenshotDeleteButtonStyle = useMemo<React.CSSProperties>(() => {
    return {
      ...screenshotIconBaseStyle,
      color: isDarkMode ? '#60a5fa' : '#2563eb',
    };
  }, [isDarkMode, screenshotIconBaseStyle]);

  const screenshotsClearButtonStyle: React.CSSProperties = {
    height: 32,
    padding: '0 12px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: '#2F58F2',
    cursor: 'pointer',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '1',
  };

  const normalizeDateKeys = (fromKey: string, toKey: string): { fromKey: string; toKey: string } => {
    const f = (fromKey ?? '').trim();
    const t = (toKey ?? '').trim();
    if (f && t && f > t) return { fromKey: t, toKey: f };
    return { fromKey: f, toKey: t };
  };

  const displayedScreenshotFromDateKey = screenshotsDateIsCustom ? screenshotFromDateKey : screenshotDefaultFromDateKey;
  const displayedScreenshotToDateKey = screenshotsDateIsCustom ? screenshotToDateKey : screenshotDefaultToDateKey;

  const onClearScreenshotsFilters = () => {
    setScreenshotCategoryFilter('All');
    setScreenshotsDateIsCustom(false);
    setScreenshotFromDateKey('');
    setScreenshotToDateKey('');
  };

  const closeScreenshotModal = () => {
    setOpenScreenshotId(null);
  };

  const computeNoteMenuAnchor = (noteId: string): { top: number; left: number } | null => {
    const btn = document.getElementById(`research-desk-note-${noteId}-menu`);
    if (!btn) return null;

    const rect = btn.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = 48;
    const margin = 8;

    let left = rect.right - menuWidth;
    left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));

    let top = rect.bottom + 6;
    if (top + menuHeight + margin > window.innerHeight) {
      top = Math.max(margin, rect.top - 6 - menuHeight);
    }

    return { top, left };
  };

  const closeNoteMenu = () => {
    setNoteMenuOpenForId(null);
    setNoteMenuAnchor(null);
  };

  const toggleNoteMenu = (noteId: string) => {
    setNoteMenuOpenForId((prev) => {
      const next = prev === noteId ? null : noteId;
      if (next) setNoteMenuAnchor(computeNoteMenuAnchor(next));
      else setNoteMenuAnchor(null);
      return next;
    });
  };

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!noteMenuOpenForId) return;

      const btn = document.getElementById(`research-desk-note-${noteMenuOpenForId}-menu`);
      if (btn && btn.contains(e.target as Node)) return;
      if (noteMenuRef.current && noteMenuRef.current.contains(e.target as Node)) return;
      closeNoteMenu();
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [noteMenuOpenForId]);

  useEffect(() => {
    if (!noteMenuOpenForId) return;

    const update = () => {
      const next = computeNoteMenuAnchor(noteMenuOpenForId);
      if (next) setNoteMenuAnchor(next);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [noteMenuOpenForId]);

  useEscapeKey(Boolean(openScreenshotId), closeScreenshotModal);

  const restoreNote = (pending: { note: ResearchDeskNote; index: number }) => {
    setNotes((prev) => {
      if (prev.some((x) => x.id === pending.note.id)) return prev;
      const next = [...prev];
      const idx = Math.max(0, Math.min(pending.index, next.length));
      next.splice(idx, 0, pending.note);
      return next;
    });
  };

  const commitPendingNoteDeleteById = async (noteId: string) => {
    const pending = pendingNoteDeleteRef.current;
    if (!pending || pending.id !== noteId) return;

    pendingNoteDeleteRef.current = null;
    setPendingNoteDelete(null);

    // Local-only mode: nothing else to do.
    if (!canPersistNotes) return;

    const token = authToken as string;
    const idNum = Number(noteId);
    if (!Number.isFinite(idNum)) {
      // Can't delete on server without numeric ID; restore.
      restoreNote({ note: pending.note, index: pending.index });
      setSyncError('Failed deleting note (invalid id)');
      return;
    }

    setSyncError(null);
    try {
      await adminApi.deleteResearchDeskNotes(token, { ids: [idNum] });
    } catch (e: any) {
      restoreNote({ note: pending.note, index: pending.index });
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      setSyncError(e?.message || 'Failed deleting note');
    }
  };

  const undoPendingNoteDelete = () => {
    const pending = pendingNoteDeleteRef.current;
    if (!pending) return;
    window.clearTimeout(pending.timerId);
    pendingNoteDeleteRef.current = null;
    setPendingNoteDelete(null);
    restoreNote({ note: pending.note, index: pending.index });
  };

  const selectedOpenScreenshot = useMemo(() => {
    if (!openScreenshotId) return null;
    return screenshots.find((s) => s.id === openScreenshotId) ?? null;
  }, [openScreenshotId, screenshots]);

  const screenshotCategoryOptions = useMemo(() => {
    const present = new Set<string>();
    for (const s of screenshots) {
      if (s?.sourcePage) present.add(s.sourcePage);
    }

    return (['All', ...SCREENSHOT_CATEGORIES.filter((c) => present.has(c))] as ScreenshotCategory[]);
  }, [screenshots]);

  // If filters change (delete/date range) and the selected category no longer exists,
  // fall back to All.
  useEffect(() => {
    if (screenshotCategoryFilter === 'All') return;
    if (!screenshotCategoryOptions.includes(screenshotCategoryFilter)) {
      setScreenshotCategoryFilter('All');
    }
  }, [screenshotCategoryFilter, screenshotCategoryOptions]);

  const filteredScreenshots = useMemo(() => {
    if (screenshotCategoryFilter === 'All') return screenshots;
    return screenshots.filter((s) => s.sourcePage === screenshotCategoryFilter);
  }, [screenshots, screenshotCategoryFilter]);

  useEffect(() => {
    if (!openScreenshotId) {
      setOpenScreenshotBusy(false);
      setOpenScreenshotError(null);
      setOpenScreenshotImageUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    if (!canPersistScreenshots) return;

    let cancelled = false;
    const run = async () => {
      setOpenScreenshotBusy(true);
      setOpenScreenshotError(null);

      try {
        const blob = await adminApi.getResearchDeskScreenshotImage(authToken as string, openScreenshotId);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setOpenScreenshotImageUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e: any) {
        if (cancelled) return;
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        setOpenScreenshotError(e?.message || 'Failed loading image');
      } finally {
        if (!cancelled) setOpenScreenshotBusy(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [openScreenshotId, canPersistScreenshots, authToken, onUnauthorized]);

  const restoreScreenshotRow = (pending: { row: ResearchDeskScreenshot; index: number }) => {
    setScreenshots((prev) => {
      if (prev.some((r) => r.id === pending.row.id)) return prev;
      const next = [...prev];
      const idx = Math.max(0, Math.min(pending.index, next.length));
      next.splice(idx, 0, pending.row);
      return next;
    });
  };

  const commitPendingScreenshotDeleteById = async (id: number) => {
    const pending = pendingScreenshotDeleteRef.current;
    if (!pending || pending.id !== id) return;

    // Hide snackbar immediately.
    pendingScreenshotDeleteRef.current = null;
    setPendingScreenshotDelete(null);

    if (!canPersistScreenshots) return;
    setDeletingScreenshotById((prev) => ({ ...prev, [id]: true }));
    setScreenshotsError(null);

    try {
      await adminApi.deleteResearchDeskScreenshots(authToken as string, { ids: [id] });
      if (openScreenshotId === id) closeScreenshotModal();
    } catch (e: any) {
      restoreScreenshotRow(pending);
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      setScreenshotsError(e?.message || 'Failed deleting screenshot');
    } finally {
      setDeletingScreenshotById((prev) => ({ ...prev, [id]: false }));
    }
  };

  const undoPendingScreenshotDelete = () => {
    const pending = pendingScreenshotDeleteRef.current;
    if (!pending) return;
    window.clearTimeout(pending.timerId);
    pendingScreenshotDeleteRef.current = null;
    setPendingScreenshotDelete(null);
    restoreScreenshotRow(pending);
  };

  const deleteScreenshot = (id: number) => {
    if (!canPersistScreenshots) return;
    if (deletingScreenshotById[id]) return;

    // If another delete is pending, commit it now (no undo).
    const existing = pendingScreenshotDeleteRef.current;
    if (existing) {
      window.clearTimeout(existing.timerId);
      void commitPendingScreenshotDeleteById(existing.id);
    }

    setScreenshotsError(null);

    const current = screenshotsRef.current;
    const removedIndex = current.findIndex((r) => r.id === id);
    const removedRow = removedIndex >= 0 ? current[removedIndex] : null;
    if (!removedRow) return;

    setScreenshots((prev) => prev.filter((r) => r.id !== id));
    if (openScreenshotId === id) closeScreenshotModal();

    const timerId = window.setTimeout(() => {
      void commitPendingScreenshotDeleteById(id);
    }, SCREENSHOT_DELETE_UNDO_MS);

    const pending: PendingScreenshotDelete = { id, row: removedRow, index: removedIndex, timerId };
    pendingScreenshotDeleteRef.current = pending;
    setPendingScreenshotDelete(pending);
  };

  useEffect(() => {
    if (!showSavedScreenshots) return;
    if (activeSubTab !== 'screenshots') return;
    if (!canPersistScreenshots) {
      setScreenshots([]);
      setScreenshotsError(null);
      return;
    }

    if (!normalizedStockSymbol) {
      setScreenshots([]);
      setScreenshotsError(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setScreenshotsBusy(true);
      setScreenshotsError(null);

      try {
        const token = authToken as string;
        const userId = resolvedUserId ?? undefined;
        const username = userId == null ? resolvedUsername || undefined : undefined;

        const useDateFilter = screenshotsDateIsCustom;

        // Always load ALL categories for the selected date range, then filter
        // client-side. This keeps the category dropdown stable and allows
        // showing only categories that actually have screenshots.
        const sourcePage = undefined;
        const rows = await adminApi.listResearchDeskScreenshots(token, {
          userId,
          username,
          sourcePage,
          fromDate: useDateFilter ? screenshotFromDateKey || undefined : undefined,
          toDate: useDateFilter ? screenshotToDateKey || undefined : undefined,
          limit: 500,
        });

        if (cancelled) return;

        const dateKeys = (rows ?? [])
          .map((r) => {
            const d = new Date(r.createdAtUtc);
            if (Number.isNaN(d.getTime())) return '';
            return d.toISOString().slice(0, 10);
          })
          .filter(Boolean)
          .sort();

        setScreenshotDefaultFromDateKey(dateKeys[0] || '');
        setScreenshotDefaultToDateKey(dateKeys.length > 0 ? dateKeys[dateKeys.length - 1] : '');

        const pendingId = pendingScreenshotDeleteRef.current?.id;
        const mapped = (rows ?? [])
          .map((r) => ({
            id: r.id,
            createdAtUtc: r.createdAtUtc,
            sourcePage: r.sourcePage,
            stockSymbol: r.stockSymbol,
            title: r.title,
            thumbDataUrl: r.thumbDataUrl,
          }))
          .filter((r) => (pendingId ? r.id !== pendingId : true))
          .filter((r) => {
            const sym = String(r.stockSymbol || '').trim().toUpperCase();
            return sym === normalizedStockSymbol || sym === 'PORTFOLIO';
          });

        setScreenshots(mapped);
      } catch (e: any) {
        if (cancelled) return;
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        setScreenshotsError(e?.message || 'Failed loading screenshots');
      } finally {
        if (!cancelled) setScreenshotsBusy(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    activeSubTab,
    canPersistScreenshots,
    authToken,
    resolvedUserId,
    resolvedUsername,
    onUnauthorized,
    normalizedStockSymbol,
    screenshotFromDateKey,
    screenshotToDateKey,
    screenshotsDateIsCustom,
  ]);

  const mainRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    alignItems: 'stretch',
  };

  const leftPanelStyle: React.CSSProperties = {
    flex: 1.12,
    display: 'flex',
    flexDirection: 'column',
    height: '350px',
  };

  const rightPanelStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% + 100px)',
  };

  const checklistBoxStyle: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    borderRadius: '6px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    padding: '0.9rem 1rem',
    flex: 1,
    minHeight: 0,
    boxSizing: 'border-box',
    height: 'auto',
    overflowY: 'visible',
  };

  const sectionRowHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  };

  const sectionLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: isDarkMode ? '#FFFFFF' : '#0D0D0D',
  };

  const countPillStyle: React.CSSProperties = {
    width: '46px',
    height: '25px',
    padding: '0 5px',
    borderRadius: '4px',
      border: `1px solid ${isDarkMode ? '#374151' : '#ECEEF4'}`,
      background: isDarkMode ? '#1F2937' : '#F6F6F6',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  const checklistGridStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    alignItems: 'flex-start',
  };

  const checklistColumnStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    minWidth: 0,
  };

  const checklistItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.55rem',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0%',
    color: isDarkMode ? '#D1D5DB' : '#1B2430',
  };

  const checklistCheckboxBaseStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
  };

  const getChecklistCheckboxStyle = (checked: boolean): React.CSSProperties => ({
    ...checklistCheckboxBaseStyle,
    backgroundColor: isDarkMode ? (checked ? '#3B82F6' : '#111827') : '#ffffff',
    border: isDarkMode ? `2px solid ${checked ? '#111827' : '#374151'}` : '2px solid #D1D5DB',
  });

  const checklistCheckboxTickStyle: React.CSSProperties = {
    fontSize: 12,
    lineHeight: 1,
    color: '#111827',
    fontWeight: 900,
  };

  const checklistInputStyle: React.CSSProperties = {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    margin: 0,
    pointerEvents: 'none',
  };

  const editorStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    border: isDarkMode ? '1px solid #374151' : '1px solid transparent',
    borderRadius: '6px',
    width: '100%',
    height: isNewNoteCollapsed ? 'auto' : '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '14px 0px',
    marginTop: '-12px',
    opacity: 1,
  };

  const notesListContainerStyle: React.CSSProperties = {
    
    paddingTop: '0rem',
  };

  const notesFrameStyle: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    borderRadius: '8px',
    padding: '1rem',
  };

  const notesListHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.5rem',
    direction: 'ltr',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: isDarkMode ? '#e5e7eb' : '#0D0D0D',
    marginBottom: '0.75rem',
  };

  const notesCountPillStyle: React.CSSProperties = {
    width: '21px',
    height: '25px',
    padding: '0 5px',
    borderRadius: '4px',
    border: `1px solid ${isDarkMode ? '#374151' : '#ECEEF4'}`,
    background: isDarkMode ? '#1F2937' : '#F6F6F6',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    boxSizing: 'border-box',
  };

  const notesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  };

  const noteCardStyle: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    borderRadius: '8px',
    padding: '0.9rem 1rem',
    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    overflow: 'hidden',
  };

  const noteMetaStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    marginBottom: '0.5rem',
  };

  const noteBodyStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    width: '100%',
  };

  const toggleChecklistItem = (label: string) => {
    setChecklist((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Initial load from server (per-user + per-stock).
  useEffect(() => {
    if (!canPersistNotes) return;

    const sym = normalizedStockSymbol;
    if (!sym) {
      setNotes([]);
      suppressChecklistSaveRef.current = true;
      setChecklist({});
      didInitFromServerRef.current = null;
      return;
    }

    const userKey = resolvedUserId != null ? String(resolvedUserId) : resolvedUsername;
    const initKey = `${userKey}:${sym}`;
    if (didInitFromServerRef.current === initKey) return;

    // Switching scope: clear UI immediately (avoid mixing symbols during load).
    setNotes([]);
    suppressChecklistSaveRef.current = true;
    setChecklist({});

    let cancelled = false;
    (async () => {
      setSyncBusy(true);
      setSyncError(null);
      try {
        const token = authToken as string;

        const [remoteNotes, remoteChecklist] = await Promise.all([
          adminApi.listResearchDeskNotes(token, {
            userId: resolvedUserId ?? undefined,
            username: resolvedUserId == null ? resolvedUsername || undefined : undefined,
            stockSymbol: sym,
          }),
          adminApi.getResearchDeskChecklist(token, {
            userId: resolvedUserId ?? undefined,
            username: resolvedUserId == null ? resolvedUsername || undefined : undefined,
            stockSymbol: sym,
          }),
        ]);

        if (cancelled) return;

        setNotes(
          (remoteNotes ?? [])
            .map((n) => ({
            id: String(n.id),
            createdAt: n.createdAtUtc,
            category: (n.associatedWith ?? '').toString(),
            title: (n.title ?? '').toString(),
            html: (n.html ?? '').toString(),
            }))
            .filter((n) => {
              const pendingId = pendingNoteDeleteRef.current?.id;
              return pendingId ? n.id !== pendingId : true;
            })
        );

        suppressChecklistSaveRef.current = true;
        setChecklist(remoteChecklist?.checklist ?? {});
        didInitFromServerRef.current = initKey;
      } catch (e: any) {
        if (cancelled) return;
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        setSyncError(e?.message || 'Failed loading Research Desk data');
      } finally {
        if (!cancelled) setSyncBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken, resolvedUserId, resolvedUsername, canPersistNotes, normalizedStockSymbol, onUnauthorized]);

  // Persist checklist with debounce.
  useEffect(() => {
    if (!canPersistNotes) return;

    const sym = normalizedStockSymbol;
    if (!sym) return;
    const userKey = resolvedUserId != null ? String(resolvedUserId) : resolvedUsername;
    const initKey = `${userKey}:${sym}`;
    if (didInitFromServerRef.current !== initKey) return;

    if (suppressChecklistSaveRef.current) {
      suppressChecklistSaveRef.current = false;
      return;
    }

    if (checklistSaveTimerRef.current) {
      window.clearTimeout(checklistSaveTimerRef.current);
    }

    checklistSaveTimerRef.current = window.setTimeout(async () => {
      try {
        const token = authToken as string;
        await adminApi.upsertResearchDeskChecklist(token, {
          userId: resolvedUserId ?? undefined,
          username: resolvedUserId == null ? resolvedUsername || undefined : undefined,
          stockSymbol: sym,
          checklist,
        });
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        setSyncError(e?.message || 'Failed saving checklist');
      }
    }, 500);

    return () => {
      if (checklistSaveTimerRef.current) {
        window.clearTimeout(checklistSaveTimerRef.current);
      }
    };
  }, [authToken, resolvedUserId, resolvedUsername, canPersistNotes, checklist, normalizedStockSymbol, onUnauthorized]);

  const formatDateDDMMYYYY = (value: string) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';

    // Prefer Date parsing (ISO etc.).
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const dd = String(parsed.getDate()).padStart(2, '0');
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const yyyy = String(parsed.getFullYear()).padStart(4, '0');
      return `${dd}/${mm}/${yyyy}`;
    }

    // Fallback for legacy stored strings like MM/DD/YYYY or DD/MM/YYYY.
    const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return raw;
    const a = Number(m[1]);
    const b = Number(m[2]);
    const yyyy = m[3];

    // Heuristic: if first part can't be month => already DD/MM.
    if (a > 12 && b <= 12) return `${m[1]}/${m[2]}/${yyyy}`;
    // If second part can't be day => treat as DD/MM.
    if (b > 12 && a <= 12) return `${m[1]}/${m[2]}/${yyyy}`;
    // Ambiguous (both <= 12): assume legacy en-US MM/DD and swap.
    return `${m[2]}/${m[1]}/${yyyy}`;
  };

  const handleAddNote = () => {
    // Legacy no-auth mode: keep local-only notes.
    void (async () => {
      const html = (noteContent ?? '').trim();
      if (!html) return;
      const now = new Date();
      const category = (noteCategory ?? '').trim() || 'General Notes';
      const title = extractTitleFromContent(noteContent);

      // Title is required for adding a new note.
      if (!title) return;

      if (canPersistNotes) {
        setSyncBusy(true);
        setSyncError(null);
        try {
          const token = authToken as string;
          const userId = resolvedUserId ?? undefined;
          const username = userId == null ? resolvedUsername || undefined : undefined;
          const stockSymbol = normalizedStockSymbol || undefined;
          const created = await adminApi.createResearchDeskNote(token, {
            userId,
            username,
            stockSymbol,
            title,
            associatedWith: category,
            html,
          });

          setNotes((prev) => [
            {
              id: String(created.id),
              createdAt: created.createdAtUtc,
              category: (created.associatedWith ?? category).toString(),
              title: created.title,
              html: created.html,
            },
            ...prev,
          ]);
          setNoteContent('');
          setNoteTitle('');
        } catch (e: any) {
          if (isUnauthorized(e)) {
            onUnauthorized?.();
            return;
          }
          setSyncError(e?.message || 'Failed saving note');
        } finally {
          setSyncBusy(false);
        }
        return;
      }

      setNotes((prev) => [
        {
          id: `${now.getTime()}-${Math.random().toString(16).slice(2)}`,
          // Store as ISO for stable parsing; format for display in the grid.
          createdAt: now.toISOString(),
          category,
          title,
          html,
        },
        ...prev,
      ]);
      setNoteContent('');
      setNoteTitle('');
    })();
  };
  const toggleNoteCollapsed = (noteId: string) => {
    setCollapsedNoteIds((prev) => ({ ...prev, [noteId]: !prev[noteId] }));

  };

  const normalizedQuery = notesSearchQuery.trim().toLowerCase();
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const toChecklistDomId = (label: string) => {
    const slug = String(label)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    return `research-desk-checklist-item-${slug || 'unknown'}`;
  };
  const visibleNotes = useMemo(() => {
    let out = notes;
    if (notesCategoryFilters.length > 0) {
      const selected = new Set(notesCategoryFilters.map((c) => (c ?? '').trim()).filter(Boolean));
      if (selected.size > 0) {
        out = out.filter((n) => selected.has((n.category ?? '').trim()));
      }
    }
    if (normalizedQuery) {
      out = out.filter((n) => {
        const text = stripHtml(n.html).toLowerCase();
        const title = (n.title ?? '').toLowerCase();
        return text.includes(normalizedQuery) || title.includes(normalizedQuery);
      });
    }
    return out;
  }, [notes, notesCategoryFilters, normalizedQuery]);

  const subNavContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    padding: '0rem 0rem 0.2rem 0rem',
    paddingLeft: '50.5px',
    borderTop: `0px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    marginBottom: '1rem',
  };

  const notesHeaderLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const notesHeaderRightStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    marginLeft: 'auto',
  };

  const notesFilterButtonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '8px',
    background: 'transparent',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    padding: 0,
    fontSize: '0.85rem',
    cursor: 'pointer',
    height: '34px',
    width: '34px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const notesSearchInputStyle: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    borderRadius: '8px',
    background: 'transparent',
    color: isDarkMode ? '#F9FAFB' : '#728197',
    padding: '0.35rem 0.7rem',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0%',
    height: '34px',
    width: '220px',
    outline: 'none',
  };

  const activeSubTabButtonStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? 'transparent' : '#0D0D0D',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.6rem 1.2rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  };

  const noteHeaderRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    paddingBottom: '0.6rem',
    borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    marginBottom: '0.6rem',
  };
  const noteHeaderLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    minWidth: 0,
  };
  const noteCaretButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
  const noteHeaderLabelStyle: React.CSSProperties = {
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    whiteSpace: 'nowrap',
  };
  const noteCategoryChipStyle: React.CSSProperties = {
    height: '30px',
    opacity: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '4px',
    border: `1px solid ${isDarkMode ? '#374151' : 'transparent'}`,
    padding: '5px 10px',
    background: isDarkMode ? '#1F2937' : '#F1F7FF',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    whiteSpace: 'nowrap',
  };
  const noteHeaderRightStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexShrink: 0,
  };
  const noteDateStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    whiteSpace: 'nowrap',
  };
  const noteHeaderDividerStyle: React.CSSProperties = {
    width: 1,
    height: 16,
    backgroundColor: isDarkMode ? '#374151' : '#d1d5db',
  };
  const noteMenuButtonStyle: React.CSSProperties = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    marginTop: -6,
    color: isDarkMode ? '#e5e7eb' : '#111827',
    fontSize: '1.2rem',
    lineHeight: 1,
  };
  const noteTitleStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: isDarkMode ? '#e5e7eb' : '#111827',
    marginBottom: '0.35rem',
    width: '100%',
  };

  const inactiveSubTabButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    border: 'none',
    padding: 0,
    fontSize: '0.95rem',
    fontWeight: 400,
    cursor: 'pointer',
  };

  const contentTitleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: 600,
    margin: '0 0 0.5rem 0',
  };

  return (
    <div id="research-desk-container" style={containerStyle}>


      <div id="research-desk-subnav" style={subNavContainerStyle}>
        <button
          type="button"
          id="research-desk-tab-notes"
          style={activeSubTab === 'notes' ? activeSubTabButtonStyle : inactiveSubTabButtonStyle}
          onClick={() => setActiveSubTab('notes')}
        >
          Notes &amp; Check List
        </button>

        {showSavedScreenshots && (
          <button
            type="button"
            id="research-desk-tab-screenshots"
            style={activeSubTab === 'screenshots' ? activeSubTabButtonStyle : inactiveSubTabButtonStyle}
            onClick={() => setActiveSubTab('screenshots')}
          >
            My Saved Screenshot
          </button>
        )}
      </div>

      {activeSubTab === 'notes' || !showSavedScreenshots ? (
        <div>
          <div style={mainRowStyle}>
            <div style={leftPanelStyle}>
              {/* CSS Styles (copied from Summary add-note-modal for exact match) */}
              <style>{`
                .note-modal-quill .quill {
                  display: flex;
                  flex-direction: column;
                  height: 100%;
                }
                .note-modal-quill .ql-toolbar {
                  order: 2;
                  border: none !important;
                  padding: 8px 0 !important;
                  display: flex !important;
                  gap: 8px !important;
                  flex-wrap: nowrap !important;
                  align-items: center !important;
                  flex-shrink: 0 !important;
                  min-height: 40px !important;
                  background: ${isDarkMode ? '#111827' : 'white'} !important;
                  margin-top: 8px !important;
                }
                .note-modal-quill .ql-container {
                  order: 1;
                  flex: 1;
                  background-color: ${isDarkMode ? '#111827' : '#ffffff'};
                  border: none !important;
                  overflow: hidden;
                }
                .note-modal-quill .ql-editor {
                  border-radius: 6px;
                  min-height: 100px;
                  max-height: 150px;
                  overflow-y: auto;
                  direction: auto;
                  text-align: start;
                  font-size: 16px;
                  color: ${isDarkMode ? '#D1D5DB' : '#0D0D0D'};
                }
                .note-modal-quill .ql-editor.ql-blank::before {
                  color: ${isDarkMode ? '#D1D5DB' : '#9CA3AF'};
                }
                .note-modal-quill .ql-editor.ql-blank {
                  background-color: ${isDarkMode ? '#111827' : 'transparent'};
                }
                .note-modal-quill .ql-editor p {
                  direction: auto;
                  text-align: start;
                }
                .note-modal-quill .ql-toolbar .ql-formats {
                  margin-right: 0 !important;
                  display: inline-flex !important;
                  gap: 22px !important;
                }
                #research-desk-new-note-toolbar button,
                #research-desk-new-note-toolbar .ql-picker {
                  width: 28px !important;
                  height: 28px !important;
                  padding: 0 !important;
                  display: inline-flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                }
                #research-desk-new-note-toolbar .ql-picker-label {
                  padding: 0 !important;
                  width: 24px !important;
                  height: 24px !important;
                }
                #research-desk-new-note-toolbar .ql-align .ql-picker-label {
                  width: 32px !important;
                  height: 32px !important;
                  padding: 0 !important;
                }
                #research-desk-new-note-toolbar .ql-align .ql-picker-label svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-align .ql-picker-label::before {
                  content: '' !important;
                  display: block !important;
                  width: 28px !important;
                  height: 28px !important;
                  background-image: url('/images/align.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-undo img,
                #research-desk-new-note-toolbar .ql-redo img {
                  width: 18px !important;
                  height: 18px !important;
                }
                #research-desk-new-note-toolbar button img {
                  width: 18px !important;
                  height: 18px !important;
                }
                #research-desk-new-note-toolbar .ql-fontDecrease img,
                #research-desk-new-note-toolbar .ql-fontIncrease img {
                  width: 24px !important;
                  height: 24px !important;
                }
                #research-desk-new-note-toolbar .ql-picker-label::before {
                  width: 16px !important;
                  height: 16px !important;
                }
                .note-modal-quill .ql-toolbar .ql-picker-options {
                  max-height: 200px !important;
                  overflow-y: auto !important;
                }
                .note-modal-quill .ql-toolbar button {
                  width: 28px !important;
                  height: 28px !important;
                  padding: 4px !important;
                }
                .note-modal-quill .ql-toolbar button svg {
                  width: 18px;
                  height: 18px;
                }
                #research-desk-new-note-toolbar .ql-mention {
                  font-size: 0 !important;
                  padding: 0 !important;
                }
                #research-desk-new-note-toolbar .ql-mention::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/mention.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-emoji {
                  font-size: 16px;
                }
                #research-desk-new-note-toolbar .ql-color .ql-picker-label,
                #research-desk-new-note-toolbar .ql-background .ql-picker-label {
                  padding: 0 !important;
                  width: 28px !important;
                  height: 28px !important;
                }
                #research-desk-new-note-toolbar .ql-color .ql-picker-label svg,
                #research-desk-new-note-toolbar .ql-background .ql-picker-label svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-color .ql-picker-label::before {
                  content: '' !important;
                  display: block !important;
                  width: 20px !important;
                  height: 20px !important;
                  background-image: url('/images/font-color.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                  margin: 4px !important;
                }
                #research-desk-new-note-toolbar .ql-background .ql-picker-label::before {
                  content: '' !important;
                  display: block !important;
                  width: 20px !important;
                  height: 20px !important;
                  background-image: url('/images/edit-editor.svg?v=20260303') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                  margin: 4px !important;
                }
                #research-desk-new-note-toolbar .ql-link svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-link::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/insert_link.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-list svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-list::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/list_check_line.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-indent svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-indent[value="-1"]::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/list-indent-increase.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-indent[value="+1"]::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/list-indent-decrease.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-bold svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-bold::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/bold_line.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-italic svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-italic::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/italic_line.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-underline svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-underline::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/underline_line.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                #research-desk-new-note-toolbar .ql-strike svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-strike::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/strikethrough_line.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
                .note-modal-quill .ql-tooltip {
                  position: absolute !important;
                  z-index: 10004 !important;
                  left: 50% !important;
                  transform: translateX(-50%) !important;
                  bottom: 50px !important;
                }
                .note-modal-quill .ql-tooltip.ql-editing {
                  left: 50% !important;
                  transform: translateX(-50%) !important;
                }
                #research-desk-new-note-toolbar .ql-emoji {
                  font-size: 0 !important;
                  padding: 0 !important;
                }
                #research-desk-new-note-toolbar .ql-emoji::before {
                  content: '' !important;
                  display: block !important;
                  width: 18px !important;
                  height: 18px !important;
                  background-image: url('/images/smile.svg') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                  font-size: 16px !important;
                }
                #research-desk-new-note-toolbar .ql-image svg {
                  display: none !important;
                }
                #research-desk-new-note-toolbar .ql-image {
                  width: 24px !important;
                  height: 24px !important;
                  min-width: 24px !important;
                  min-height: 24px !important;
                  padding: 0 !important;
                  overflow: visible !important;
                }
                #research-desk-new-note-toolbar .ql-image::before {
                  content: '' !important;
                  display: block !important;
                  width: 24px !important;
                  height: 24px !important;
                  margin-top: 6px !important;
                  background-image: url('/images/insertimage.svg?v=20260303') !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-position: center !important;
                }
              `}</style>

              <div id="research-desk-new-note" style={editorStyle}>
                {/* Header (inline, no overlay) */}
                <div
                  id="research-desk-new-note-header"
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <button
                    type="button"
                    id="research-desk-new-note-toggle"
                    onClick={() => setIsNewNoteCollapsed((v) => !v)}
                    aria-label={isNewNoteCollapsed ? 'Expand New Note' : 'Collapse New Note'}
                    title={isNewNoteCollapsed ? 'Expand' : 'Collapse'}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/images/arrow.svg"
                      alt=""
                      style={{
                        width: 18,
                        height: 18,
                        transform: isNewNoteCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                      }}
                    />
                  </button>
                  <h2
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '18px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                      margin: 0,
                    }}
                  >
                    New Note
                  </h2>
                </div>

                {/* Content Wrapper with Border */}
                {!isNewNoteCollapsed && (
                  <div
                    id="research-desk-new-note-content-wrapper"
                    style={{
                      border: `1px solid ${isDarkMode ? '#374151' : '#ECECF4'}`,
                      borderRadius: '6px',
                      padding: '15px',
                      paddingBottom: '0px',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: '6px',
                      overflow: 'visible',
                    }}
                  >
                  {/* Editor Content */}
                  <div
                    id="research-desk-new-note-editor"
                    ref={newNoteEditorRef}
                    style={{
                      flex: 1,
                      overflow: 'visible',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div className="note-modal-quill" style={{ height: '214px', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ flex: 1, minHeight: 0 }}>
                        <ReactQuill
                          ref={quillRef}
                          theme="snow"
                          value={noteContent}
                          onChange={setNoteContent}
                          placeholder="Here you can write about research desk"
                          style={{
                            height: '100%',
                            border: 'none',
                          }}
                          modules={quillModules}
                          formats={quillFormats}
                        />
                      </div>
                      <div
                        id="research-desk-new-note-toolbar"
                        style={{
                          paddingTop: '8px',
                          paddingBottom: '12px',
                          marginTop: '24px',
                          display: 'flex',
                          gap: '24px',
                          flexWrap: 'nowrap',
                          background: 'white',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          width: '100%',
                          borderBottom: isDarkMode ? 'none' : '1px solid #ECECF4',
                          marginBottom: '12px',
                        }}
                      >
                        <span className="ql-formats">
                          <button className="ql-undo" title="Undo">
                            <img src="/images/undo-icon.svg" alt="Undo" style={{ width: '18px', height: '18px' }} />
                          </button>
                          <button className="ql-redo" title="Redo">
                            <img
                              src="/images/undo-icon.svg"
                              alt="Redo"
                              style={{ width: '18px', height: '18px', transform: 'scaleX(-1)' }}
                            />
                          </button>
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '1px',
                            height: '20px',
                            backgroundColor: '#D0D0D0',
                            margin: '0 8px',
                            flexShrink: 0,
                          }}
                        ></span>
                        <span className="ql-formats">
                          <button className="ql-list" value="ordered" title="Ordered List"></button>
                          <button className="ql-list" value="bullet" title="Bullet List"></button>
                        </span>
                        <span className="ql-formats">
                          <select className="ql-align" title="Text Alignment"></select>
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '1px',
                            height: '20px',
                            backgroundColor: '#D0D0D0',
                            margin: '0 8px',
                            flexShrink: 0,
                          }}
                        ></span>
                        <span className="ql-formats">
                          <button className="ql-indent" value="+1" title="Indent Increase"></button>
                          <button className="ql-indent" value="-1" title="Indent Decrease"></button>
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '1px',
                            height: '20px',
                            backgroundColor: '#D0D0D0',
                            margin: '0 8px',
                            flexShrink: 0,
                          }}
                        ></span>
                        <span className="ql-formats">
                          <select className="ql-color" title="Text Color"></select>
                          <select className="ql-background" title="Background Color"></select>
                        </span>
                        <span className="ql-formats">
                          <button
                            className="ql-fontDecrease"
                            title="Decrease Font"
                            onClick={(e) => {
                              e.preventDefault();
                              const quill = quillRef.current?.getEditor?.();
                              applyQuillFontSizeStep(quill, -1);
                            }}
                          >
                            <img
                              src="/images/a-arrow-down.svg"
                              alt="A-"
                              style={{ width: '24px', height: '24px' }}
                            />
                          </button>
                          <button
                            className="ql-fontIncrease"
                            title="Increase Font"
                            onClick={(e) => {
                              e.preventDefault();
                              const quill = quillRef.current?.getEditor?.();
                              applyQuillFontSizeStep(quill, 1);
                            }}
                          >
                            <img
                              src="/images/a-arrow-up.svg"
                              alt="A+"
                              style={{ width: '24px', height: '24px' }}
                            />
                          </button>
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '1px',
                            height: '20px',
                            backgroundColor: '#D0D0D0',
                            margin: '0 8px',
                            flexShrink: 0,
                          }}
                        ></span>
                        <span className="ql-formats">
                          <button className="ql-bold" title="Bold"></button>
                          <button className="ql-italic" title="Italic"></button>
                          <button className="ql-underline" title="Underline"></button>
                          <button className="ql-strike" title="Strike"></button>
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '1px',
                            height: '20px',
                            backgroundColor: '#D0D0D0',
                            margin: '0 8px',
                            flexShrink: 0,
                          }}
                        ></span>
                        <span className="ql-formats">
                          <button className="ql-link" title="Insert Link"></button>
                          <button
                            className="ql-mention"
                            title="Mention (@)"
                            onClick={(e) => {
                              e.preventDefault();
                              const quill = document.querySelector('.note-modal-quill .ql-editor') as any;
                              if (quill) {
                                const selection = window.getSelection();
                                if (selection && selection.rangeCount > 0) {
                                  const range = selection.getRangeAt(0);
                                  const textNode = document.createTextNode('@');
                                  range.insertNode(textNode);
                                  range.setStartAfter(textNode);
                                  range.setEndAfter(textNode);
                                }
                              }
                            }}
                          >
                            @
                          </button>
                          <button
                            className="ql-emoji"
                            title="Emoji"
                            onClick={(e) => {
                              e.preventDefault();
                              const quill = document.querySelector('.note-modal-quill .ql-editor') as any;
                              if (quill) {
                                const selection = window.getSelection();
                                if (selection && selection.rangeCount > 0) {
                                  const range = selection.getRangeAt(0);
                                  const textNode = document.createTextNode('😊');
                                  range.insertNode(textNode);
                                  range.setStartAfter(textNode);
                                  range.setEndAfter(textNode);
                                }
                              }
                            }}
                          >
                            😊
                          </button>
                          <button className="ql-image" title="Insert Image"></button>
                        </span>
                      </div>
                    </div>

                    {/* Separator Line Below Toolbar */}
                    <div
                      style={{
                        width: '100%',
                        height: '1px',
                        backgroundColor: isDarkMode ? '#374151' : '#ECECF4',
                        margin: '1px 0',
                      }}
                    ></div>
                  </div>

                  {/* Footer */}
                  <div
                    id="research-desk-new-note-footer"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '1rem',
                      borderBottom: isDarkMode ? 'none' : '1px solid #ECECF4',
                      marginTop: '0rem',
                      padding: '0.5rem',
                      marginLeft: '-1rem',
                      marginRight: '-1rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div ref={noteCategoryDropdownRef} style={noteCategoryDropdownWrapStyle}>
                        <button
                          id="research-desk-new-note-category"
                          type="button"
                          style={noteCategoryDropdownButtonStyle}
                          onClick={() => setIsNoteCategoryOpen((v) => !v)}
                          aria-haspopup="listbox"
                          aria-expanded={isNoteCategoryOpen}
                        >
                          {noteCategory || 'Associated With:'}
                        </button>
                        <span aria-hidden="true" style={noteCategoryDropdownArrowStyle}>
                          <img src="/images/arrow.svg" alt="" style={{ width: 18, height: 18, display: 'block' }} />
                        </span>
                        {isNoteCategoryOpen && (
                          <div role="listbox" aria-label="Associated With" style={noteCategoryDropdownMenuStyle}>
                            {NOTE_CATEGORIES.map((c, idx) => {
                              const selectedCategory = (noteCategory ?? '').trim();
                              const checked = selectedCategory ? selectedCategory === c : c === 'General Notes';
                              const isLast = idx === NOTE_CATEGORIES.length - 1;
                              return (
                                <label
                                  key={c}
                                  style={{
                                    ...noteCategoryDropdownItemStyle,
                                    borderBottom: isLast ? 'none' : `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
                                  }}
                                >
                                  <span
                                    onClick={() => {
                                      setNoteCategory(c);
                                      setIsNoteCategoryOpen(false);
                                    }}
                                    style={{ cursor: 'pointer', marginRight: 8 }}
                                  >
                                    {renderNoteCategoryCheckbox(checked)}
                                  </span>
                                  <span>{c}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      id="research-desk-new-note-add"
                      disabled={!canAddNote}
                      onClick={handleAddNote}
                      style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        border: '1px solid #ECECF4',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: canAddNote ? 'pointer' : 'not-allowed',
                        opacity: canAddNote ? 1 : 0.6,
                      }}
                    >
                      Add Note
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>

            <div id="research-desk-checklist-panel" style={rightPanelStyle}>
              <div id="research-desk-checklist-header" style={{ ...sectionRowHeaderStyle, marginBottom: '0.5rem' }}>
                <div style={{ ...sectionLabelStyle, gap: '8px' }}>
                  <button
                    type="button"
                    id="research-desk-checklist-toggle"
                    onClick={() => setIsChecklistCollapsed((v) => !v)}
                    aria-label={isChecklistCollapsed ? 'Expand Check List' : 'Collapse Check List'}
                    title={isChecklistCollapsed ? 'Expand' : 'Collapse'}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/images/arrow.svg"
                      alt=""
                      style={{
                        width: 18,
                        height: 18,
                        transform: isChecklistCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                      }}
                    />
                  </button>
                  <span>Check List</span>
                  <span id="research-desk-checklist-count" style={countPillStyle}>
                    {checkedCount}/{checklistItems.length}
                  </span>
                </div>
              </div>

              {!isChecklistCollapsed && (
                <div id="research-desk-checklist-box" style={checklistBoxStyle}>
                  <div style={checklistGridStyle}>
                    <div style={checklistColumnStyle}>
                      {checklistItems.slice(0, 7).map((label) => (
                        <label key={label} id={`${toChecklistDomId(label)}-label`} style={checklistItemStyle}>
                          <input
                            type="checkbox"
                            id={toChecklistDomId(label)}
                            checked={!!checklist[label]}
                            onChange={() => toggleChecklistItem(label)}
                            style={checklistInputStyle}
                          />
                          <span style={getChecklistCheckboxStyle(!!checklist[label])}>
                            {checklist[label] ? <span style={checklistCheckboxTickStyle}>✓</span> : null}
                          </span>
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>

                    <div style={checklistColumnStyle}>
                      {checklistItems.slice(7).map((label) => (
                        <label key={label} id={`${toChecklistDomId(label)}-label`} style={checklistItemStyle}>
                          <input
                            type="checkbox"
                            id={toChecklistDomId(label)}
                            checked={!!checklist[label]}
                            onChange={() => toggleChecklistItem(label)}
                            style={checklistInputStyle}
                          />
                          <span style={getChecklistCheckboxStyle(!!checklist[label])}>
                            {checklist[label] ? <span style={checklistCheckboxTickStyle}>✓</span> : null}
                          </span>
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div id="research-desk-notes-list-container" style={notesListContainerStyle}>
            <div id="research-desk-notes-list" style={notesFrameStyle}>
              {(syncBusy || syncError) && (
                <div
                  style={{
                    marginBottom: '0.75rem',
                    padding: '0.6rem 0.8rem',
                    borderRadius: '8px',
                    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                    color: syncError ? '#ef4444' : (isDarkMode ? '#9ca3af' : '#6b7280'),
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}
                >
                  {syncError ? syncError : 'Syncing…'}
                </div>
              )}

              <div id="research-desk-all-notes-header" style={notesListHeaderStyle}>
                <div style={notesHeaderLeftStyle}>
                  <span
                    id="research-desk-all-notes-title"
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '18px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                    }}
                  >
                    All Notes
                  </span>
                  <span id="research-desk-all-notes-count" style={notesCountPillStyle}>{visibleNotes.length}</span>
                </div>

                <div style={notesHeaderRightStyle}>
                  <div ref={notesFilterPopoverRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      id="research-desk-all-notes-sort"
                      style={notesFilterButtonStyle}
                      onClick={() => setIsNotesCategoryFilterOpen((v) => !v)}
                      title={notesCategoryFilters.length > 0 ? `Filtering by: ${notesCategoryFilters.join(', ')}` : 'Filter notes by category'}
                      aria-label="Filter notes"
                    >
                      <img
                        src="/images/sort.svg"
                        alt=""
                        style={{ width: 24, height: 24, display: 'block', filter: isDarkMode ? 'invert(1)' : 'none' }}
                      />
                    </button>
                    {visibleNotes.length > 0 && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '4px',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#2F58F2',
                          borderRadius: '999px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Manrope, sans-serif',
                          fontWeight: 600,
                          fontSize: '8px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: isDarkMode ? '#111827' : '#FFFFFF',
                          transform: 'translateX(4px)',
                          boxSizing: 'border-box',
                        }}
                      >
                        {visibleNotes.length}
                      </span>
                    )}

                    {isNotesCategoryFilterOpen && (
                      <div
                        id="research-desk-notes-filter-window"
                        style={notesFilterDropdownMenuStyle}
                      >
                        <label
                          style={{
                            ...notesFilterDropdownItemStyle,
                            borderBottom: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
                          }}
                        >
                          <span
                            onClick={() => setNotesCategoryFilters([])}
                            style={{ cursor: 'pointer', marginRight: 8 }}
                          >
                            {renderNotesFilterCheckbox(notesCategoryFilters.length === 0)}
                          </span>
                          <span style={{ flex: '1 1 auto', textAlign: 'left' }}>All</span>
                        </label>

                        {NOTE_CATEGORIES.map((c, idx) => {
                          const checked = notesCategoryFilters.includes(c);
                          const isLast = idx === NOTE_CATEGORIES.length - 1;
                          return (
                            <label
                              key={c}
                              style={{
                                ...notesFilterDropdownItemStyle,
                                borderBottom: isLast ? 'none' : `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
                              }}
                            >
                              <span
                                onClick={() => setNotesCategoryFilters((prev) =>
                                  prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
                                )}
                                style={{ cursor: 'pointer', marginRight: 8 }}
                              >
                                {renderNotesFilterCheckbox(checked)}
                              </span>
                              <span style={{ flex: '1 1 auto', textAlign: 'left' }}>{c}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <style>{`
                      #research-desk-all-notes-search::placeholder {
                        font-family: Manrope, sans-serif;
                        font-weight: 700;
                        font-size: 14px;
                        line-height: 20px;
                        letter-spacing: 0%;
                        color: #728197;
                      }
                    `}</style>
                    <img
                      src="/images/search-sm.svg"
                      alt=""
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      id="research-desk-all-notes-search"
                      type="text"
                      value={notesSearchQuery}
                      onChange={(e) => setNotesSearchQuery(e.target.value)}
                      placeholder="Search notes"
                      style={{
                        ...notesSearchInputStyle,
                        paddingLeft: '30px',
                      }}
                    />
                  </div>
                </div>
              </div>

              {visibleNotes.length === 0 ? (
                <div
                  style={{
                    padding: '1.5rem 1rem',
                    textAlign: 'center',
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    fontSize: '0.95rem',
                  }}
                >
                  No notes yet.
                </div>
              ) : (
                <div style={notesGridStyle}>
                  {visibleNotes.map((n) => (
                    <div key={n.id} id={`research-desk-note-${n.id}`} style={noteCardStyle}>
                      <div style={noteHeaderRowStyle}>
                        <div style={noteHeaderLeftStyle}>
                          <button
                            type="button"
                            style={noteCaretButtonStyle}
                            aria-label={collapsedNoteIds[n.id] ? 'Expand note' : 'Collapse note'}
                            title={collapsedNoteIds[n.id] ? 'Expand' : 'Collapse'}
                            onClick={() => toggleNoteCollapsed(n.id)}
                          >
                            <img
                              src="/images/arrow.svg"
                              alt=""
                              style={{
                                width: 16,
                                height: 16,
                                transform: collapsedNoteIds[n.id] ? 'rotate(-90deg)' : 'rotate(0deg)',
                                display: 'block',
                              }}
                            />
                          </button>
                          <span id={`research-desk-note-${n.id}-label`} style={noteHeaderLabelStyle}>Note Associated with</span>
                          <span id={`research-desk-note-${n.id}-category`} style={noteCategoryChipStyle}>{(n.category || '').replace(/^Associated with\s+/i, '') || '—'}</span>
                        </div>

                        <div id={`research-desk-note-${n.id}-header-right`} style={noteHeaderRightStyle}>
                          <span id={`research-desk-note-${n.id}-date`} style={noteDateStyle}>{formatDateDDMMYYYY(n.createdAt)}</span>
                          <span style={noteHeaderDividerStyle} />
                          <button
                            type="button"
                            id={`research-desk-note-${n.id}-menu`}
                            style={noteMenuButtonStyle}
                            aria-label="More options"
                            title="More"
                            aria-haspopup="menu"
                            aria-expanded={noteMenuOpenForId === n.id}
                            aria-controls={`research-desk-note-${n.id}-menu-items`}
                            onClick={() => toggleNoteMenu(n.id)}
                          >
                            …
                          </button>
                        </div>
                      </div>

                      {!collapsedNoteIds[n.id] && (
                        <>
                          {(() => {
                            const titleDir = detectTextDirection(n.title, 'ltr');
                            const bodyDir = detectTextDirection(n.html, titleDir);
                            const effectiveDir: 'rtl' | 'ltr' = titleDir === 'rtl' || bodyDir === 'rtl' ? 'rtl' : 'ltr';
                            const align = effectiveDir === 'rtl' ? 'right' : 'left';

                            return (
                              <>
                                <div
                                  id={`research-desk-note-${n.id}-title`}
                                  dir={effectiveDir}
                                  style={{
                                    ...noteTitleStyle,
                                    direction: effectiveDir,
                                    textAlign: align,
                                    unicodeBidi: 'plaintext',
                                  }}
                                >
                                  {n.title || '—'}
                                </div>

                                <div
                                  id={`research-desk-note-${n.id}-body`}
                                  className="ql-editor"
                                  dir={effectiveDir}
                                  style={{
                                    ...noteBodyStyle,
                                    direction: effectiveDir,
                                    textAlign: align,
                                    unicodeBidi: 'plaintext',
                                    padding: 0,
                                  }}
                                  dangerouslySetInnerHTML={{ __html: n.html }}
                                />
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {noteMenuOpenForId && noteMenuAnchor && (
                <>
                  {(() => {
                    const menuId = `research-desk-note-${noteMenuOpenForId}-menu-items`;
                    const noteId = noteMenuOpenForId;

                    const menuStyle: React.CSSProperties = {
                      position: 'fixed',
                      top: noteMenuAnchor.top,
                      left: noteMenuAnchor.left,
                      width: 180,
                      borderRadius: 10,
                      border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                      background: isDarkMode ? '#0b1220' : '#ffffff',
                      boxShadow: '0 18px 46px rgba(15,23,42,0.18)',
                      padding: '6px',
                      zIndex: 12000,
                    };

                    const menuItemStyle: React.CSSProperties = {
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      color: isDarkMode ? '#e5e7eb' : '#111827',
                      borderRadius: 8,
                      textAlign: 'left',
                    };

                    const iconBox = (color: string): React.CSSProperties => ({
                      width: 18,
                      height: 18,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color,
                      fontWeight: 900,
                      flex: '0 0 auto',
                    });

                    return createPortal(
                      <div ref={noteMenuRef} id={menuId} role="menu" aria-label="Note actions" style={menuStyle}>
                        <button
                          type="button"
                          role="menuitem"
                          style={menuItemStyle}
                          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.04)' : '#f3f4f6')}
                          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')}
                          onClick={() => {
                            closeNoteMenu();

                            const noteIndex = notes.findIndex((x) => x.id === noteId);
                            const note = noteIndex >= 0 ? notes[noteIndex] : null;
                            if (!note) return;

                            // If another delete is pending, commit it now (no undo).
                            const existing = pendingNoteDeleteRef.current;
                            if (existing) {
                              window.clearTimeout(existing.timerId);
                              void commitPendingNoteDeleteById(existing.id);
                            }

                            setNotes((prev) => prev.filter((x) => x.id !== noteId));
                            setCollapsedNoteIds((prev) => {
                              if (!(noteId in prev)) return prev;
                              const next = { ...prev };
                              delete next[noteId];
                              return next;
                            });

                            const timerId = window.setTimeout(() => {
                              void commitPendingNoteDeleteById(noteId);
                            }, NOTE_DELETE_UNDO_MS);

                            const pending: PendingNoteDelete = { id: noteId, note, index: noteIndex, timerId };
                            pendingNoteDeleteRef.current = pending;
                            setPendingNoteDelete(pending);
                          }}
                        >
                          <span style={iconBox('#FF0052')}>×</span>
                          <span>Delete</span>
                        </button>
                      </div>,
                      document.body
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={screenshotsHeaderRowStyle}>
              <div style={screenshotsLeftControlsStyle}>
                <div style={screenshotsCategorySelectWrapStyle}>
                  <div ref={screenshotsCategoryPopoverRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      id="research-desk-screenshots-category"
                      style={screenshotsCategoryDropdownButtonStyle}
                      onClick={() => setIsScreenshotsCategoryOpen((v) => !v)}
                      aria-label="Category"
                      aria-haspopup="listbox"
                      aria-expanded={isScreenshotsCategoryOpen}
                    >
                      {screenshotCategoryFilter}
                    </button>
                    {isScreenshotsCategoryOpen && (
                      <div role="listbox" aria-label="Category" style={screenshotsCategoryDropdownMenuStyle}>
                        {screenshotCategoryOptions.map((c, idx) => {
                          const checked = screenshotCategoryFilter === c;
                          const isLast = idx === screenshotCategoryOptions.length - 1;
                          return (
                            <label
                              key={c}
                              style={{
                                ...screenshotsCategoryDropdownItemStyle,
                                fontWeight: checked ? 700 : 400,
                                borderBottom: isLast ? 'none' : `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                borderRadius: 0,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setScreenshotCategoryFilter(c as ScreenshotCategory);
                                  setIsScreenshotsCategoryOpen(false);
                                }}
                                style={screenshotsCategoryDropdownRadioStyle}
                              />
                              <span style={{ flex: '1 1 auto', textAlign: 'left' }}>{c}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <span aria-hidden="true" style={screenshotsCategorySelectArrowStyle}>
                    <img src="/images/arrow.svg" alt="" style={{ width: 18, height: 18, display: 'block' }} />
                  </span>
                </div>
              </div>

              <div style={screenshotsControlsStyle}>
                <input
                  id="research-desk-screenshots-from-date"
                  type="date"
                  value={displayedScreenshotFromDateKey}
                  onChange={(e) => {
                    const next = normalizeDateKeys(e.target.value, displayedScreenshotToDateKey);
                    setScreenshotsDateIsCustom(true);
                    setScreenshotFromDateKey(next.fromKey);
                    setScreenshotToDateKey(next.toKey);
                  }}
                  style={screenshotsDateInputStyle}
                  aria-label="From date"
                />
                <input
                  id="research-desk-screenshots-to-date"
                  type="date"
                  value={displayedScreenshotToDateKey}
                  onChange={(e) => {
                    const next = normalizeDateKeys(displayedScreenshotFromDateKey, e.target.value);
                    setScreenshotsDateIsCustom(true);
                    setScreenshotFromDateKey(next.fromKey);
                    setScreenshotToDateKey(next.toKey);
                  }}
                  style={screenshotsDateInputStyle}
                  aria-label="To date"
                />
                <button
                  id="research-desk-screenshots-clear"
                  type="button"
                  onClick={onClearScreenshotsFilters}
                  style={screenshotsClearButtonStyle}
                  title="Clear"
                >
                  Clear
                </button>
              </div>
            </div>

            {screenshotsError && (
              <div
                style={{
                  border: `1px solid ${isDarkMode ? '#7f1d1d' : '#FCA5A5'}`,
                  borderRadius: 10,
                  padding: '0.75rem 1rem',
                  color: isDarkMode ? '#FCA5A5' : '#991B1B',
                  background: isDarkMode ? '#111827' : '#FFF1F2',
                }}
              >
                {screenshotsError}
              </div>
            )}

            {screenshotsBusy ? (
              <div
                style={{
                  border: `1px dashed ${isDarkMode ? '#374151' : '#D9D9D9'}`,
                  borderRadius: 10,
                  padding: '1rem',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  background: isDarkMode ? '#0b1220' : '#ffffff',
                }}
              >
                Loading screenshots…
              </div>
            ) : filteredScreenshots.length === 0 ? (
              <div
                style={{
                  border: `1px dashed ${isDarkMode ? '#374151' : '#D9D9D9'}`,
                  borderRadius: 10,
                  padding: '1rem',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  background: isDarkMode ? '#0b1220' : '#ffffff',
                }}
              >
                No screenshots yet.
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(535px, 1fr))',
                  gap: '12px',
                  justifyItems: 'start',
                }}
              >
                {filteredScreenshots.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      width: 'min(580px, 100%)',
                      height: 404,
                      border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                      borderRadius: 6,
                      padding: '10px 20px',
                      background: isDarkMode ? '#0b1220' : '#ffffff',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        color: isDarkMode ? '#E5E7EB' : '#111827',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                        <button
                          type="button"
                          aria-label="Open full image"
                          title="Open"
                          style={screenshotExpandButtonStyle}
                          onClick={() => setOpenScreenshotId(s.id)}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M14 5h5v5"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10 19H5v-5"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M19 5l-7 7"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 19l7-7"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>

                        <span
                          title="Source tab"
                          style={{
                            fontFamily: 'Manrope, sans-serif',
                            fontWeight: 500,
                            fontStyle: 'normal',
                            fontSize: '16px',
                            lineHeight: '100%',
                            letterSpacing: '0%',
                            color: '#0D0D0D',
                            transform: 'translateY(-2px)',
                            flex: '0 0 auto',
                          }}
                        >
                          {s.sourcePage}
                        </span>

                        <span
                          aria-hidden="true"
                          style={{
                            width: '1px',
                            height: '18px',
                            background: isDarkMode ? 'rgba(229, 231, 235, 0.22)' : '#E5E7EB',
                            flex: '0 0 auto',
                          }}
                        />

                        <span
                          title={`${s.stockSymbol} • ${s.title}`}
                          style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {s.title || '—'}
                        </span>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          color: isDarkMode ? '#9ca3af' : '#6b7280',
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          flex: '0 0 auto',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                            fontWeight: 300,
                            fontSize: 14,
                            color: isDarkMode ? '#E5E7EB' : '#0D0D0D',
                          }}
                        >
                          {formatDateDDMMYYYY(s.createdAtUtc)}
                        </span>
                        <button
                          type="button"
                          aria-label="Remove screenshot"
                          title="Remove"
                          style={{
                            ...screenshotDeleteButtonStyle,
                            opacity: deletingScreenshotById[s.id] ? 0.6 : 1,
                            pointerEvents: deletingScreenshotById[s.id] ? 'none' : 'auto',
                          }}
                          onClick={() => deleteScreenshot(s.id)}
                        >
                          <img
                            src="/images/bookmark_fill.svg"
                            alt="Remove"
                            style={{ width: '18px', height: '18px', display: 'block' }}
                          />
                        </button>
                      </div>
                    </div>

                    <div
                      style={{ ...screenshotThumbFrameStyle, cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      aria-label="Open full image"
                      title="Open"
                      onClick={() => setOpenScreenshotId(s.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenScreenshotId(s.id);
                        }
                      }}
                    >
                      <img
                        src={s.thumbDataUrl}
                        alt={`${s.stockSymbol} ${s.title}`}
                        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {openScreenshotId && (
              <div
                id="research-desk-screenshot-modal-overlay"
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  zIndex: 10000,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '2rem',
                }}
                onClick={closeScreenshotModal}
              >
                <div
                  id="research-desk-screenshot-modal-container"
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: 12,
                    padding: '2rem',
                    width: '90%',
                    maxWidth: '1200px',
                    height: '85vh',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    id="research-desk-screenshot-modal-close"
                    onClick={closeScreenshotModal}
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      background: 'none',
                      border: 'none',
                      fontSize: '2rem',
                      cursor: 'pointer',
                      color: '#6b7280',
                      fontWeight: 'bold',
                      padding: '0.25rem 0.5rem',
                      lineHeight: '1',
                      zIndex: 10001,
                    }}
                    title="Close"
                    aria-label="Close"
                  >
                    ×
                  </button>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                      paddingRight: 36,
                      marginBottom: '0.75rem',
                      color: '#111827',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{selectedOpenScreenshot?.title || 'Screenshot'}</div>
                      <div style={{ fontSize: '0.82rem', opacity: 0.8 }}>
                        {selectedOpenScreenshot
                          ? `${selectedOpenScreenshot.stockSymbol} • ${selectedOpenScreenshot.sourcePage} • ${formatDateDDMMYYYY(
                              selectedOpenScreenshot.createdAtUtc
                            )}`
                          : ''}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      flex: '1 1 auto',
                      borderRadius: 12,
                      border: '1px solid #F3F4F6',
                      background: '#F9FAFB',
                      padding: '0.5rem',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                      minHeight: 0,
                    }}
                  >
                    {openScreenshotBusy ? (
                      <div style={{ color: '#6b7280' }}>Loading…</div>
                    ) : openScreenshotError ? (
                      <div style={{ color: '#991B1B' }}>{openScreenshotError}</div>
                    ) : openScreenshotImageUrl ? (
                      <div
                        style={{
                          transform: 'scale(1)',
                          transformOrigin: 'center center',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={openScreenshotImageUrl}
                          alt={selectedOpenScreenshot?.title || 'Screenshot'}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: 10,
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ color: '#6b7280' }}>No image</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'notes' && pendingNoteDelete && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            zIndex: 11000,
            width: 450,
            height: 46,
            opacity: 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 13,
              paddingRight: 10,
              paddingBottom: 13,
              paddingLeft: 10,
              boxSizing: 'border-box',
              width: '100%',
              height: '100%',
              borderRadius: 8,
              background: '#F1F7FF',
              color: '#2F58F2',
              boxShadow: '0 12px 40px rgba(17, 24, 39, 0.18)',
            }}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>Note has been removed</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button
                type="button"
                onClick={undoPendingNoteDelete}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#2F58F2',
                }}
                title="Undo"
              >
                <img src="/images/undo.svg" alt="Undo" style={{ width: 18, height: 18, display: 'block' }} />
                <span>Undo</span>
              </button>

              <button
                type="button"
                aria-label="Dismiss"
                title="Close"
                onClick={() => {
                  window.clearTimeout(pendingNoteDelete.timerId);
                  void commitPendingNoteDeleteById(pendingNoteDelete.id);
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  lineHeight: 1,
                  color: '#2F58F2',
                  opacity: 0.9,
                }}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

            {activeSubTab === 'screenshots' && pendingScreenshotDelete && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  position: 'fixed',
                  left: '50%',
                  bottom: 24,
                  transform: 'translateX(-50%)',
                  zIndex: 11000,
                  width: 450,
                  height: 46,
                  opacity: 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 13,
                    paddingRight: 10,
                    paddingBottom: 13,
                    paddingLeft: 10,
                    boxSizing: 'border-box',
                    width: '100%',
                    height: '100%',
                    borderRadius: 8,
                    background: '#F1F7FF',
                    color: '#2F58F2',
                    boxShadow: '0 12px 40px rgba(17, 24, 39, 0.18)',
                  }}
                >
                  <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>Screenshot has been removed</div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <button
                      type="button"
                      onClick={undoPendingScreenshotDelete}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#2F58F2',
                      }}
                      title="Undo"
                    >
                      <img src="/images/undo.svg" alt="Undo" style={{ width: 18, height: 18, display: 'block' }} />
                      <span>Undo</span>
                    </button>

                    <button
                      type="button"
                      aria-label="Dismiss"
                      title="Close"
                      onClick={() => {
                        window.clearTimeout(pendingScreenshotDelete.timerId);
                        void commitPendingScreenshotDeleteById(pendingScreenshotDelete.id);
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        color: '#2F58F2',
                        opacity: 0.9,
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            )}
    </div>
  );
};

export default ResearchDeskScreen;
